import { useEffect, useState } from 'react';

import { loadTemplateSession } from './planTemplateSource';
import { renderOrder } from './loggerUnits';
import { useLocale } from '../primitives/useLocale';
import type { DraftEntry } from './state/loggerDraftTypes';
import type { ProgramApiFetch } from '../ProgramEditor/types';

// Render-time resolver for the PER-EXERCISE + PER-SET coach cues (the 💬 entry
// instruction and the 🎯 SetPrescription note), keyed by exercise slug.
//
// This is the SINGLE display source for plan cues (note-model consolidation,
// 2026-07-03): the draft never carries them. The cues live on the program/plan
// template only, are display-only, and are never snapshotted onto the saved
// session (feedback_no_denormalized_display_snapshots). Resolving here (not
// from the draft) makes them correct by construction across fresh-start / EDIT
// / REPEAT AND across an in-workout exercise swap — the cue follows the slug,
// so a swapped-in exercise shows ITS cue (or none), never the replaced one's.
//
// WHERE the template comes from is planTemplateSource's job (cache-first, then
// the server — LG-30). This hook only turns it into the slug→cues map; it
// never reads storage or the network itself, and never touches the draft /
// save path.

export type PlanEntryCues = {
  /** Per-exercise instruction (slot instructions → entry note, 💬). */
  instruction?: string;
  /** Per-set cues (🎯), index-aligned with the planned sets. */
  perSetNotes: string[];
};

// The minimal shape both sources expose: a program-resolved session entry and a
// single-day Plan entry are field-compatible for cue extraction.
type CueSourceEntry = {
  exerciseId: string;
  notes?: string;
  sets: ReadonlyArray<{ notes?: string }>;
};

// Build the slug→cues map from either source's entries. LG-34: the map keeps
// EVERY occurrence of a slug, in planned order, instead of keeping the first
// and dropping the rest. The D4 of JL Full Daily has three `walking` slots —
// warm-up, HIIT, cool-down — and "first entry per slug wins" showed the
// warm-up's text on all three, which made the HIIT protocol and the cool-down
// unreachable in the Logger. That text can be a safety constraint, so this was
// never only cosmetic.
//
// The plan side is positional by construction: a PlannedExerciseEntry has no
// id (LG-40 — plan entries resolve by position) and the template's list is
// flat, with no groups, so its own array order IS the authored order. Nothing
// to reorder here; `cueForEntry` below is what maps a draft entry onto it.
const cuesFromEntries = (
  entries: ReadonlyArray<CueSourceEntry>,
): Map<string, PlanEntryCues[]> => {
  const map = new Map<string, PlanEntryCues[]>();
  for (const e of entries) {
    const cues: PlanEntryCues = {
      ...(e.notes ? { instruction: e.notes } : {}),
      perSetNotes: e.sets.map((s) => s.notes ?? ''),
    };
    const seen = map.get(e.exerciseId);
    if (seen) seen.push(cues);
    else map.set(e.exerciseId, [cues]);
  }
  return map;
};

/** The draft entries of one exercise, in drawn order: the rank among them is
 *  the key a plan cue (and a model slot) is matched by. */
export const entriesOfSlug = (entries: DraftEntry[], exerciseId: string): DraftEntry[] =>
  renderOrder(entries).filter((e) => e.exerciseId === exerciseId);

// Resolve ONE draft entry's cues. The key is deliberately not an id: slots used
// to be born with `id: ""` (PE-47; PE-49b/c minted one on the last such paths),
// and `rowIdOf` still falls back to the slug for any entry without one. So the
// pair (slug, rank-among-same-slug) is the
// key, and the rank is read by REFERENCE off the drawn order.
//
// ⛔ `renderOrder`, never `draft.entries` raw. LG-45 decided on 2026-09-07 that
// renderOrder is the one true order after three surfaces were found reading
// three different ones; counting occurrences on the raw array would have made
// this the fourth. `renderOrder` returns the same objects, so `indexOf` is an
// identity lookup and needs no id at all.
//
// Resolving by slug is what makes an in-workout swap correct (LG-30): a
// swapped-in exercise has its own slug and therefore its own list, so it shows
// ITS cue or none — never the replaced one's. When the draft holds more
// occurrences of a slug than the plan authored (added by hand mid-workout),
// the last authored cue is reused rather than showing nothing.
export const cueForEntry = (
  cues: Map<string, PlanEntryCues[]>,
  entries: DraftEntry[],
  entry: DraftEntry | null | undefined,
): PlanEntryCues | undefined => {
  if (!entry) return undefined;
  const authored = cues.get(entry.exerciseId);
  if (!authored || authored.length === 0) return undefined;
  const rank = entriesOfSlug(entries, entry.exerciseId).indexOf(entry);
  if (rank < 0) return authored[0];
  return authored[rank] ?? authored[authored.length - 1];
};

export function usePlanCues(
  fromPlanId: string | undefined,
  apiFetch: ProgramApiFetch,
): Map<string, PlanEntryCues[]> {
  const lang = useLocale();
  const [cues, setCues] = useState<Map<string, PlanEntryCues[]>>(
    () => new Map(),
  );

  useEffect(() => {
    if (!fromPlanId) {
      setCues(new Map());
      return;
    }
    let alive = true;
    void (async () => {
      const session = await loadTemplateSession(fromPlanId, apiFetch, lang);
      if (!alive || !session) return;
      setCues(cuesFromEntries(session.entries));
    })();
    return () => {
      alive = false;
    };
  }, [fromPlanId, apiFetch, lang]);

  return cues;
}
